"use client"

import { formatTurfTotalRange, type ArtificialTurfCostResult } from "@/lib/artificial-turf-cost-calculator"
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { DollarSign, TrendingDown, CheckCircle, Crown, Hammer } from "lucide-react"

interface ArtificialTurfCostResultsProps {
  result: ArtificialTurfCostResult
}

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`
const usdRange = (r: { low: number; high: number }) =>
  r.low === r.high ? usd(r.low) : `${usd(r.low)} to ${usd(r.high)}`

export function ArtificialTurfCostResults({ result }: ArtificialTurfCostResultsProps) {
  if (!result || !result.region) {
    return null
  }

  const methodLabel = result.installMethod === "diy" ? "DIY" : "Professional"
  const maxBudget = Math.max(...result.budgetBreakdown.map((b) => b.high), 1)

  return (
    <div className="space-y-6">
      {/* DIY vs Professional Comparison */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-green-600" />
            DIY vs professional
          </CardTitle>
          <CardDescription>
            Same {result.area.toLocaleString()} sq ft job, two ways to install it. Ranges are estimates, not quotes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`text-center p-5 rounded-xl border ${
                result.installMethod === "diy"
                  ? "bg-linear-to-br from-green-100 to-green-50 border-2 border-green-400"
                  : "bg-linear-to-br from-green-50 to-gray-50 border-green-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Hammer className="w-4 h-4 text-green-700" />
                <p className="text-sm text-gray-600">DIY (materials + rental)</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-700 tabular-nums">{formatTurfTotalRange(result.diyTotal)}</p>
              <p className="text-xs text-gray-500 mt-2">No labor line, your weekend</p>
            </div>

            <div
              className={`text-center p-5 rounded-xl border ${
                result.installMethod === "professional"
                  ? "bg-linear-to-br from-green-100 to-green-50 border-2 border-green-400"
                  : "bg-linear-to-br from-green-50 to-gray-50 border-green-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-green-700" />
                <p className="text-sm text-gray-600">Professional (installed)</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-700 tabular-nums">{formatTurfTotalRange(result.proTotal)}</p>
              <p className="text-xs text-gray-500 mt-2">Crew handles the full install</p>
            </div>
          </div>
        </CardContent>
      </div>

      {/* Cost Breakdown */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Where the money goes
          </CardTitle>
          <CardDescription>
            Estimated cost by line item for your {methodLabel.toLowerCase()} install
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {result.budgetBreakdown.map((item) => (
            <div
              key={item.category}
              className="p-4 bg-linear-to-br from-green-50/60 to-gray-50 rounded-xl border border-green-100"
            >
              <div className="flex justify-between items-baseline gap-3 mb-2">
                <div>
                  <h4 className="text-base font-semibold text-green-900">{item.category}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                </div>
                <p className="text-sm font-semibold text-green-700 tabular-nums whitespace-nowrap">
                  {usdRange({ low: item.low, high: item.high })}
                  <span className="text-xs font-normal text-gray-500 ml-2">{item.percentage.toFixed(0)}%</span>
                </p>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-1.5 rounded-full bg-green-500 transition-[width]"
                  style={{ width: `${(item.high / maxBudget) * 100}%` }}
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500">
            Percentages are based on the midpoint of each range. Local quotes commonly land outside these ranges depending
            on base depth, yard access, and crew.
          </p>
        </CardContent>
      </div>

      {/* Cost Saving Tips */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-green-600" />
            Ways to spend less
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {result.costSavingTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-3 text-sm p-2 bg-green-50/50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </div>
    </div>
  )
}
